import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	ModalContent,
	ModalControls,
	DateInput,
	TimeInput,
	ModalHeader,
	Button,
	Input,
	Label,
	Row,
	Column,
	ButtonGroup,
	Toggle,
	Text,
	Tooltip,
	Icon
} from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import moment from 'moment';
import { pick } from 'lodash';

import translate from '../../Helpers/translate';
import UpSelect from 'Components/Inputs/UpSelect';
import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import { ACTIVITY_PRIORITY } from 'App/babel/enum/activity';
import { makeCancelable, CancelablePromise } from '../../../helpers/promise';
import { mapProperties } from 'Services/ActionProperties';
import { setupComponentCompatibility } from 'App/helpers/angularPortingHelpers';
import { addNotification, STYLE as notificationStyle } from 'Store/reducers/SystemNotificationReducer';
import store from 'App/babel/store';
import ProjectResource from 'App/resources/Project';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';

import './CreateActivity.scss';
import AppService from 'App/legacy/AppService';

export const TABS = {
	USER: 'user',
	ROLE: 'role'
};

export const COOKIE_PROP = 'actNotification';

const CAMPAIGN_LIMIT = 10;

type User = { id: string | number; name: string; role?: { id: number } };
type Role = { id: number; name: string; users: User[] };

type CreateActivityProps = {
	reject: () => void;
	resolve: () => void;
	reloadModalPosition?: () => void;
	meta: {
		activityTypes: { id: number; name: string }[];
		activeUsers: User[];
		roles: Role[];
		activeUserId: number;
	};
	filters: any;
	extraParams?: any;
	name?: string | null;
	entity: string;
	customerId: number;
	numSelected?: number;
};

const CreateActivity = (props: CreateActivityProps) => {
	const { meta, filters, extraParams, name, entity, customerId, numSelected, reject, resolve } = props;
	const { activeUserId, activeUsers = [], roles = [] } = meta;

	// When opened from React advanced search, numSelected is passed via props.
	// When opened from Angular advanced search, fall back to Angular's $multiSelect.
	const selected = (() => {
		if (numSelected != null) return numSelected;
		try {
			const $multiSelect = getAngularModule('$multiSelect');
			return $multiSelect.selected.length || $multiSelect.getTotal();
		} catch {
			return 0;
		}
	})();

	const users = useMemo(
		() => [
			{ id: '{{Client.UserId}}', name: translate('default.accountManager') },
			{ id: '{{Client.AssignedUserId}}', name: translate('automationTerms.assignedUser') },
			...activeUsers.map(user => ({ ...user, id: String(user.id) }))
		],
		[activeUsers]
	);

	const rolesWithUsers = useMemo(
		() =>
			roles.map(role => ({
				...role,
				users: activeUsers.filter(user => role.id === (user.role && user.role.id))
			})),
		[roles, activeUsers]
	);

	const isMultiSelect = selected > 1;
	const defaultSelectedTime = useRef(moment().format('HH:mm')).current;
	const cancelablePromise = useRef<CancelablePromise<any> | null>(null);

	const [currentTab, setCurrentTab] = useState(TABS.USER);
	const [saving, setSaving] = useState(false);
	const [touched, setTouched] = useState<Record<string, boolean>>({ Description: false });
	const [activity, setActivity] = useState({
		Description: '',
		Date: new Date() as Date | null,
		Time: '',
		ActivityType: AppService.getMetadata().params.DefaultActivityTypeId,
		User: isMultiSelect ? [String(activeUserId)] : String(activeUserId),
		Project: '',
		AssignToManager: 0,
		AssignToUser: 0,
		SendNotification: Number(!document.cookie.includes(COOKIE_PROP)),
		Priority: ACTIVITY_PRIORITY.NONE
	});
	const [defaultUsers, setDefaultUsers] = useState<any>(
		isMultiSelect ? [String(activeUserId)] : String(activeUserId)
	);

	useEffect(() => {
		return () => {
			cancelablePromise.current?.cancel();
		};
	}, []);

	const lang = {
		create: translate('default.create'),
		activities: translate('default.activities'),
		description: translate('default.description'),
		activityType: translate('activity_type'),
		date: translate('default.date'),
		time: translate('default.time'),
		user: translate('user'),
		campaign: translate('campaign'),
		cancel: translate('cancel'),
		describeActivity: translate('activity.describeActivity'),
		activity: translate('activity'),
		searchCampaigns: translate('activity.searchCampaigns'),
		toUserOrRole: translate('activity.toUserOrRole'),
		toUserOrRoleTooltip: translate('activity.toUserOrRole.tooltip'),
		role: translate('default.role'),
		selectUser: translate('default.selectUser'),
		selectRole: translate('default.selectRole'),
		searchRoles: translate('activity.searchRoles'),
		searchUsers: translate('activity.searchUsers'),
		giveToAccountManager: translate('activity.giveToAccountManager'),
		giveToAssignedUser: translate('activity.giveToAssignedUser'),
		sendNotification: translate('activity.sendNotification'),
		noResults: translate('default.noResults'),
		priority: translate('activity.priority'),
		high: translate('activity.high')
	};

	const onChange = (event: any, key: string) => {
		setActivity(prev => ({ ...prev, [key]: event.target.value }));
	};

	const onBlur = (_event: any, key: string) => {
		setTouched(prev => ({ ...prev, [key]: true }));
	};

	const onUserChange = ({ target }: any) => {
		const newDefaultUsers = isMultiSelect
			? users.filter(user => target.value.includes(user.id)).map(({ id, name }) => ({ id: String(id), name }))
			: target.value;

		setDefaultUsers(newDefaultUsers);
		setActivity(prev => ({ ...prev, User: target.value }));
	};

	const onRoleChange = (event: any) => {
		const role = rolesWithUsers.find(role => role.id === parseInt(event.target.value));
		const roleUsers = role?.users || [];

		setActivity(prev => ({ ...prev, User: roleUsers.map(({ id }) => Number(id)) as any }));
	};

	const onDateChange = (event: any) => {
		const isValid = moment(event.target.value).isValid();
		setActivity(prev => ({ ...prev, Date: isValid ? event.target.value : null }));
	};

	const onToggleChange = (toggleName: string, checked: boolean | number) => {
		setActivity(prev => ({ ...prev, [toggleName]: Number(checked) }));
	};

	const onNotificationChange = (checked: boolean) => {
		onToggleChange('SendNotification', checked);
		if (!document.cookie.includes(COOKIE_PROP)) {
			document.cookie = `${COOKIE_PROP}=0`;
		}
	};

	const handleTabChange = (tab: string) => {
		let user: any = null;
		if (tab === TABS.USER) {
			user = isMultiSelect ? [String(activeUserId)] : String(activeUserId);
		}

		setCurrentTab(tab);
		setDefaultUsers(isMultiSelect ? [String(activeUserId)] : String(activeUserId));
		setActivity(prev => ({ ...prev, User: user }));
	};

	const setUserSelection = (_el: any, cb: (val: any) => void) => {
		if (!activity.User) return cb(null);
		if (!Array.isArray(activity.User)) {
			return cb(users.find(user => activity.User === user.id));
		}
		const selectedUsers = users.filter(user => (activity.User as any[]).includes(user.id));
		cb(selectedUsers);
	};

	const campaignTransport = (query: any) => {
		if (query.data) {
			const rb = new RequestBuilder();
			rb.fields = ['id', 'name'];
			rb.addFilter({ field: 'active' }, comparisonTypes.Equals, true);
			rb.addFilter({ field: 'name' }, comparisonTypes.Search, query.data.term);
			rb.addSort({ field: 'name' }, true);
			rb.limit = CAMPAIGN_LIMIT;
			rb.offset = (query.data.page - 1) * CAMPAIGN_LIMIT;

			return ProjectResource.find(rb.build()).then((res: any) => query.success(res));
		}
		return query.success({ data: [] });
	};

	const save = () => {
		const MultiActions = getAngularModule('MultiActions');
		const activityData = pick(activity, (val: any) => !!val) as any;

		setSaving(true);

		if (activityData.Date) {
			activityData.Date = moment(activityData.Date).format('YYYY-MM-DD');
		}

		// WEB-3203: Set CreateForEachUser to false for mass activity creation to distribute round-robin
		activityData.CreateForEachUser = false;

		const properties = mapProperties(activityData);

		// For the Angular path, inject selected IDs into the filters.
		// The React path already has the correct ID filters baked into the RequestBuilder.
		if (!numSelected) {
			try {
				const $multiSelect = getAngularModule('$multiSelect');
				const selectedIds = $multiSelect.selected;
				if (selectedIds && selectedIds.length) {
					filters.addFilter({ field: 'id' }, filters.comparisonTypes.Equals, selectedIds);
				}
			} catch {
				// Not in Angular context
			}
		}

		cancelablePromise.current = makeCancelable(
			MultiActions.customer(customerId)
				.createActivity(
					entity,
					selected,
					properties,
					filters.build(),
					name || '',
					extraParams ? extraParams : {}
				)
				.then(() => {
					if (selected < 20) {
						(store.dispatch as any)(
							addNotification({
								style: notificationStyle.SUCCESS,
								icon: 'check',
								title: 'default.created',
								body: 'saved.activities'
							})
						);
					} else {
						(store.dispatch as any)(
							addNotification({
								style: notificationStyle.SUCCESS,
								icon: 'check',
								title: 'default.created',
								body: 'saveQued.activities'
							})
						);
					}
				})
		);

		return cancelablePromise.current.promise
			.then(() => {
				setSaving(false);
				// When opened from Angular, clear Angular's multiselect state
				if (!numSelected) {
					try {
						getAngularModule('$multiSelect').selectNone();
					} catch {
						// Not in Angular context
					}
				}
				resolve();
			})
			.catch((error: any) => {
				if (error && error.isCanceled) {
					return;
				}

				(store.dispatch as any)(
					addNotification({
						style: notificationStyle.ERROR,
						icon: 'times',
						title: 'default.error',
						body: 'saveError.activities'
					})
				);

				setSaving(false);
			});
	};

	const className = new BemClass('CreateActivityModal');
	const {
		ActivityType,
		Date: activityDate,
		Time,
		Description,
		AssignToManager,
		AssignToUser,
		SendNotification,
		Priority,
		User
	} = activity;
	const { activityTypes = [] } = meta;
	const hasUser = Array.isArray(User) ? User.length > 0 : !!User;
	const isValid = !!activityDate && !!Description && hasUser;

	const campaignOptions = {
		ajax: {
			transport: campaignTransport,
			data: (term: string, page: number) => ({ term, page }),
			results: (res: any, page: number) => {
				const offset = (page - 1) * CAMPAIGN_LIMIT;
				const more = offset + CAMPAIGN_LIMIT < res.metadata.total;
				return { results: res.data, more };
			}
		},
		formatNoMatches: () => lang.noResults
	};

	return (
		<div id="create-acrivity" className={className.b()}>
			<ModalHeader title={`${lang.create} ${lang.activities}`} onClose={reject} />
			<ModalContent>
				<Row direction="row">
					<Label required>{lang.description}</Label>
					<Input
						autofocus
						className={className.elem('Form-control').b()}
						disabled={saving}
						placeholder={lang.describeActivity}
						onChange={event => onChange(event, 'Description')}
						onBlur={event => onBlur(event, 'Description')}
						state={touched['Description'] && !Description ? 'error' : undefined}
					/>
				</Row>
				<Row direction="row" className="column-justify">
					<Column size={6}>
						<Label>{lang.activityType}</Label>
						<UpSelect
							className={className.elem('Form-control').b()}
							getId={(x: any = {}) => x.id}
							formatResult={(x: any = {}, container: any, query: any, escape: any) => escape(x.name)}
							formatSelection={(x: any = {}, container: any, escape: any) => escape(x.name)}
							onChange={(event: any) => onChange(event, 'ActivityType')}
							data={activityTypes}
							disabled={saving}
							defaultValue={ActivityType}
						/>
					</Column>

					<Column size={5.5}>
						<Label>{lang.priority}</Label>
						<div className={className.elem('Priority').b()}>
							<Toggle
								color="red"
								size="lg"
								onChange={checked =>
									onToggleChange(
										'Priority',
										checked ? ACTIVITY_PRIORITY.HIGH : ACTIVITY_PRIORITY.NONE
									)
								}
								checked={!!Priority}
							/>
							<Text>{lang.high}</Text>
						</div>
					</Column>
				</Row>
				<Row direction="row">
					<Label required>{lang.date}</Label>
					<div className={className.elem('Form-control', 'Date-area').b()}>
						<Column size={4}>
							<DateInput
								closeOnSelect
								placeholder={lang.date}
								value={activityDate}
								onChange={onDateChange}
								disabled={saving}
								state={!activityDate ? 'error' : undefined}
								calendarSize={6}
							/>
						</Column>
						<Column size={8}>
							<TimeInput
								placeholder={lang.time}
								defaultSelected={defaultSelectedTime}
								value={Time}
								onChange={event => onChange(event, 'Time')}
								disabled={saving}
								state={!activityDate ? 'error' : undefined}
							/>
						</Column>
					</div>
				</Row>
				<Row>
					<div className={className.elem('user-or-role-label').b()}>
						<Label>{lang.toUserOrRole}</Label>
						<Tooltip title={lang.toUserOrRoleTooltip}>
							<Icon name="question-circle" />
						</Tooltip>
					</div>
					<ButtonGroup className={className.elem('Form-control').b()}>
						{Object.keys(TABS).map(tab => (
							<Button
								key={tab}
								size="sm"
								onClick={() => handleTabChange((TABS as any)[tab])}
								color={currentTab === (TABS as any)[tab] ? 'bright-blue' : 'light-grey'}
							>
								{(lang as any)[(TABS as any)[tab]]}
							</Button>
						))}
					</ButtonGroup>
				</Row>
				{currentTab === TABS.USER ? (
					<Row direction="row">
						<Label required>{lang.selectUser}</Label>
						<UpSelect
							required
							multiple={isMultiSelect}
							className={className.elem('Form-control').b() + (hasUser ? '' : ' has-error')}
							getId={(x: any = {}) => x.id}
							formatResult={(x: any = {}, container: any, query: any, escape: any) => escape(x.name)}
							formatSelection={(x: any = {}, container: any, escape: any) => escape(x.name)}
							disabled={saving}
							onChange={onUserChange}
							data={users}
							initSelection={setUserSelection}
							placeholder={lang.searchUsers}
							defaultValue={defaultUsers}
							selectedValues={
								isMultiSelect && Array.isArray(User) ? (User as (string | number)[]) : undefined
							}
						/>
					</Row>
				) : null}

				{currentTab === TABS.ROLE ? (
					<Row direction="row">
						<Label required>{lang.selectRole}</Label>
						<UpSelect
							required
							className={className.elem('Form-control').b() + (hasUser ? '' : ' has-error')}
							getId={(x: any = {}) => x.id}
							formatResult={(x: any = {}, container: any, query: any, escape: any) =>
								`<div class="${className.elem('Result-wrapper')}">
									<span>${escape(x.name)}</span>
									<span class="${className.elem('Users-count')}">${x.users.length} users</span>
								</div>`
							}
							formatSelection={(x: any = {}, container: any, escape: any) => escape(x.name)}
							disabled={saving}
							onChange={onRoleChange}
							data={rolesWithUsers}
							placeholder={lang.searchRoles}
						/>
					</Row>
				) : null}
				<Row direction="row">
					<Label>{lang.campaign}</Label>
					<UpSelect
						className={className.elem('Form-control').b()}
						getId={(x: any = {}) => x.id}
						formatResult={(x: any = {}, container: any, query: any, escape: any) => escape(x.name)}
						formatSelection={(x: any = {}, container: any, escape: any) => escape(x.name)}
						disabled={saving}
						placeholder={lang.searchCampaigns}
						onChange={(event: any) => onChange(event, 'Project')}
						options={campaignOptions}
					/>
				</Row>
				<Row>
					<Toggle
						size="xs"
						onChange={checked => onToggleChange('AssignToManager', checked)}
						checked={!!AssignToManager}
					/>
					<Text>{lang.giveToAccountManager}</Text>
				</Row>
				<Row>
					<Toggle
						size="xs"
						onChange={checked => onToggleChange('AssignToUser', checked)}
						checked={!!AssignToUser}
					/>
					<Text>{lang.giveToAssignedUser}</Text>
				</Row>
				<Row>
					<Toggle size="xs" onChange={onNotificationChange} checked={!!SendNotification} />
					<Text>{lang.sendNotification}</Text>
				</Row>
			</ModalContent>
			<ModalControls>
				<Button
					shadow="none"
					color={isValid ? 'bright-blue' : 'light-grey'}
					onClick={save}
					disabled={!isValid}
					loading={saving}
				>
					{lang.create} {selected}{' '}
					{isMultiSelect ? lang.activities.toLowerCase() : lang.activity.toLowerCase()}
				</Button>
				<Button onClick={reject} shadow="none" type="link" color="grey">
					{lang.cancel}
				</Button>
			</ModalControls>
		</div>
	);
};

export const CreateActivityModal = setupComponentCompatibility(CreateActivity, {
	modalName: 'CreateActivityModal',
	modalSize: 'sm',
	modalParamsMapper: ($modalParams: any) => $modalParams,
	setupMetaInit: async () => ({
		activeUsers: AppService.getActiveUsers(),
		activeUserId: AppService.getSelf().id,
		activityTypes: AppService.getActivityTypes('activity', true),
		activityCustomFields: AppService.getCustomFields('activity'),
		roles: AppService.getRoles()
	})
});

export default CreateActivity;
